/**
 * Ransomware identifier — UI layer.
 *
 * The person on the other side of this page has just lost their files and is
 * frightened. Two things follow from that and shape everything here:
 *
 *   1. The answer must never be more confident than the evidence. A single
 *      family name is only shown when a single family matched; otherwise the
 *      candidates are listed, because the next thing anyone does with a name is
 *      search for a decryptor, and that search is full of malware wearing the
 *      name of a real tool.
 *   2. The advice that matters is the same whether we identify the family or
 *      not — keep the encrypted files, keep the note, do not reformat. So it is
 *      shown either way, not as a consolation prize for a failed match.
 */

import { buildIndex, identify, looksEncrypted } from './ransom-match.js';

const T = (k, p) => {
  let s = (window.__RID_I18N && window.__RID_I18N[k]) || k;
  if (p) for (const a of Object.keys(p).sort((x, y) => y.length - x.length)) s = s.split(':' + a).join(p[a]);
  return s;
};
const el = (id) => document.getElementById(id);
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const state = { index: null, loading: null, files: [] };

/* The database is 170 KB and most visits never need it, so it is fetched when
 * the person actually asks something — not on page open. */
async function db() {
  if (state.index) return state.index;
  if (!state.loading) {
    state.loading = fetch(window.__RID_DB, { credentials: 'omit' })
      .then((r) => { if (!r.ok) throw new Error(`db_${r.status}`); return r.json(); })
      .then((j) => { state.index = buildIndex(j); return state.index; });
  }
  return state.loading;
}

function show(html) { const o = el('rid-out'); o.innerHTML = html; o.hidden = false; }

/** Read a text file, but only as much of it as the matching can use. */
async function readNote(file) {
  const slice = file.slice(0, 64 * 1024);
  const buf = new Uint8Array(await slice.arrayBuffer());
  // A note is text. If it decodes with replacement characters everywhere it is
  // not a note, it is one of the encrypted files.
  const text = new TextDecoder('utf-8', { fatal: false }).decode(buf);
  const junk = (text.match(/\uFFFD/g) || []).length;
  return { text, head: buf, isText: junk < text.length * 0.02 };
}

const NOTE_EXT = /\.(txt|html?|hta|rtf|md|url|lnk)$/i;

async function run() {
  const index = await db().catch(() => null);
  if (!index) { show(card('bad', T('verdict_db_failed'), T('body_db_failed'))); return; }

  const typed = (el('rid-name') && el('rid-name').value || '').trim();
  const pasted = (el('rid-text') && el('rid-text').value || '').trim();

  let noteName = null, noteText = pasted || null, encryptedName = null, encrypted = null;

  for (const f of state.files) {
    const r = await readNote(f);
    if (r.isText && (NOTE_EXT.test(f.name) || r.text.length > 80)) {
      noteName = noteName || f.name;
      noteText = noteText || r.text;
    } else {
      encryptedName = encryptedName || f.name;
      encrypted = encrypted || looksEncrypted(r.head, f.size);
    }
  }
  // A single dropped file is whichever it turned out to be; if it is a note its
  // own name is also worth trying as an extension and vice versa.
  if (!encryptedName && state.files.length === 1 && !noteName) encryptedName = state.files[0].name;

  if (!noteName && !noteText && !encryptedName && !typed) {
    show(card('warn', T('verdict_nothing_given'), T('body_nothing_given')));
    return;
  }

  const res = identify(index, { noteName, noteText, encryptedName, familyName: typed || null });
  render(res, { encrypted, noteName, encryptedName });
}

const card = (tone, title, body, extra = '') =>
  `<div class="rid-card rid-tone-${tone}"><p class="rid-title">${title}</p>
     <p class="rid-body">${body}</p>${extra}</div>`;

const block = (title, inner, cls = '') =>
  `<section class="rid-block ${cls}"><h3>${title}</h3>${inner}</section>`;

function signalText(s) {
  return {
    note: T('sig_note'), notePattern: T('sig_note_pattern'), extension: T('sig_extension'),
    marker: T('sig_marker'), name: T('sig_name'),
  }[s.signal] || s.signal;
}

function familyCard(m) {
  const f = m.family;
  const how = [...new Set(m.signals.map(signalText))].join(' · ');
  const dec = (f.decryptors || []).map((d) =>
    `<li><a href="${esc(d.url)}" target="_blank" rel="noopener nofollow">${esc(d.vendor)}</a>${
      d.name ? ` — ${esc(d.name)}` : ''}</li>`).join('');
  return `<article class="rid-fam${f.decryptors && f.decryptors.length ? ' rid-fam-good' : ''}">
      <h4>${esc(f.name)}</h4>
      <p class="rid-how">${how}</p>
      ${dec
      ? `<p class="rid-good">${T('has_decryptor')}</p><ul class="rid-links">${dec}</ul>`
      : `<p class="rid-none">${T('no_decryptor', { date: state.index.db.built })}</p>`}
    </article>`;
}

function render(res, ctx) {
  window.__stage && window.__stage('analyzed');
  const steps = `<ol class="rid-steps">${
    [1, 2, 3, 4, 5].map((i) => `<li>${T(`step_${i}`)}</li>`).join('')}</ol>`;

  let head;
  if (!res.matches.length) {
    head = card('warn', T('verdict_unknown'), T('body_unknown'),
      `<p class="rid-body"><a href="https://id-ransomware.malwarehunterteam.com/" target="_blank"
         rel="noopener nofollow">${T('try_idr')}</a></p>`);
  } else if (res.matches.length === 1) {
    head = card('ok', T('verdict_one', { name: res.matches[0].family.name }), T('body_one'));
  } else {
    head = card('warn', T('verdict_several', { n: String(res.matches.length) }), T('body_several'));
  }

  const fams = res.matches.length
    ? block(T('candidates_title'), res.matches.slice(0, 8).map(familyCard).join(''), 'rid-fams') : '';

  /* Said only when a file was actually examined, and phrased as what the bytes
   * look like — not as a verdict on the machine. */
  let enc = '';
  if (ctx.encrypted) {
    enc = block(T('file_title'), `<p class="rid-body">${
      ctx.encrypted.encrypted ? T('file_encrypted')
        : ctx.encrypted.reason === 'known_signature'
          ? T('file_intact', { kind: ctx.encrypted.kind })
          : T('file_unclear')}</p>`);
  }

  show(head + fams + enc
    + block(T('steps_title'), steps, 'rid-warn')
    + `<p class="rid-src">${T('data_from', { date: res.built })}</p>`);

  /* ⚠️ What goes to telemetry, and what must never: the note carries the
   * victim's own case ID and often their employer's name in the filename.
   * Only family-level facts leave the page — never a filename, never a byte of
   * the note. The extension is family-level by nature, so it is the one thing
   * worth keeping when nothing matched: it is how we learn what the database
   * is missing. */
  const ext = (ctx.encryptedName || '').toLowerCase().match(/(\.[a-z0-9_+-]{1,24})$/);
  window.__aeToolComplete && window.__aeToolComplete({
    matched: res.matches.length,
    confidence: res.confidence,
    family: res.matches.length === 1 ? res.matches[0].family.id : null,
    decryptable: res.decryptable ? 1 : 0,
    signals: [...new Set(res.matches.flatMap((m) => m.signals.map((s) => s.signal)))].join(','),
    gave: res.used.join(','),
  });
  if (!res.matches.length) {
    window.__aeDiagnostic && window.__aeDiagnostic({
      issue: 'not_identified', ext: ext ? ext[1] : null, gave: res.used.join(','),
    });
  }
}

function fileList() {
  const box = el('rid-files');
  if (!box) return;
  box.innerHTML = state.files.map((f) => `<li>${esc(f.name)}</li>`).join('');
  box.hidden = !state.files.length;
}

document.addEventListener('DOMContentLoaded', () => {
  const zone = el('ridDrop');
  if (zone) {
    zone.addEventListener('tool-uploader:file', (ev) => {
      const d = ev.detail || {};
      const incoming = d.files ? [...d.files] : (d.file ? [d.file] : []);
      for (const f of incoming) if (!state.files.some((x) => x.name === f.name && x.size === f.size)) state.files.push(f);
      state.files = state.files.slice(0, 8);
      fileList();
      window.__stage && window.__stage('file_select');
      run();
    });
  }
  const go = el('rid-go');
  if (go) go.addEventListener('click', () => { window.__stage && window.__stage('submit'); run(); });

  for (const id of ['rid-name', 'rid-text']) {
    const f = el(id);
    if (f) f.addEventListener('keydown', (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); run(); } });
  }
});

export { run };
